import { Box, Flex } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { SettingButton } from 'components/common/settings-dialog/button';
import { SettingForm } from 'components/common/settings-dialog/form';
import { SettingRow } from 'components/common/settings-dialog/row';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { first } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { MachinesService } from 'services/machines.service';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  onClose: () => void;
}

export const MachineSwitchTab = (props: IProps) => {
  const navigate = useNavigate();
  const authLoading = useAuthStore((s) => s.loading);

  const { loading: machineLoading, machine } = useContext(MachineContext);

  const [options, setOptions] = useState<IOption[]>([]);

  // load all machines for the user's team and assemble the options
  useEffect(() => {
    MachinesService.getInstance()
      .getTeamMachines()
      .then((machines) => {
        machines.sort((a, b) =>
          StringHelper.safeSort(a.machineID, b.machineID)
        );

        setOptions(
          machines
            .filter(
              // only show every other machine as an option
              (m) => m.machineID !== machine.machineID
            )
            .filter(
              // disallow switching to deleted machines (e.g. machine has been returned)
              (m) => !m.deleted
            )
            .map((m) => {
              const o: IOption = {
                label: MachineContextHelper.getMachineMenuLabel(m),
                value: m.machineID,
              };

              return o;
            })
        );
      });
  }, [machine.machineID]);

  // for selecting machineID via a search input
  const [values, setValues] = useState<string[]>([]);

  const loading = authLoading || machineLoading;

  return (
    <ErrorBoundary identifier="MachineSwitchTab">
      <SettingForm>
        <SettingRow
          header="settings.switch-machine"
          description={t('settings.switch-machine-msg')}
          input={
            <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
              <CommonSearchInput
                id="switch-machineID"
                name="switch-machineID"
                disabled={loading}
                values={values}
                options={options}
                onChange={(v) => setValues(v)}
                optional
              />
              <Box>
                <SettingButton
                  label="settings.switch-machine"
                  disabled={loading || values.length === 0}
                  onClick={async () => {
                    const machineID = first(values);

                    if (!machineID) {
                      return;
                    }

                    const user = await useAuthStore.getState().updateUser({
                      machineID: machineID,
                    });

                    if (!user) {
                      return;
                    }

                    navigate(paths.pitches.lists);

                    /** rotating the new token will provide a new session id since machine has changed */
                    // will trigger destroySocket > createSocket
                    await useAuthStore.getState().rotateTokens();

                    props.onClose();
                  }}
                />
              </Box>
            </Flex>
          }
        />
      </SettingForm>
    </ErrorBoundary>
  );
};
